import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';

// dBFS thresholds for zone boundaries (floor: −60 dB)
const DB_FLOOR = -60;
const GREEN_MAX_DB = -20; // green  : −60 → −20 dBFS
const RED_MIN_DB = -8;    // orange : −20 → −8 dBFS  |  red: −8 → 0 dBFS

function dbToPos(db: number): number {
  return ((db - DB_FLOOR) / -DB_FLOOR) * 100;
}

const GREEN_PCT = dbToPos(GREEN_MAX_DB);            // ≈ 66.7 %
const ORANGE_PCT = dbToPos(RED_MIN_DB) - GREEN_PCT; // ≈ 20.0 %
// red takes the remaining width automatically

// Asymmetric smoothing coefficients (applied per animation frame, ~60 fps)
const ATTACK = 0.85;   // fraction of raw value used on rising edge  → reaches peak in ~2 frames
const RELEASE = 0.08;  // fraction of raw value used on falling edge → ~400 ms decay

// Peak-hold: frames to freeze the marker, then per-frame decay multiplier
const PEAK_HOLD_FRAMES = 90; // ≈ 1.5 s at 60 fps
const PEAK_DECAY = 0.95;     // multiplier applied each frame after hold expires

@Component({
  selector: 'app-audio-level-indicator',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      role="meter"
      aria-label="Audio input level"
      [attr.aria-valuenow]="levelPercent()"
      aria-valuemin="0"
      aria-valuemax="100"
      class="relative h-2.5 w-40 rounded-sm overflow-hidden"
    >
      <!-- Always-visible zones (lit): green / orange / red -->
      <div aria-hidden="true" class="absolute inset-0 flex">
        <div class="h-full bg-green-500" [style.width.%]="greenPct"></div>
        <div class="h-full bg-orange-400" [style.width.%]="orangePct"></div>
        <div class="h-full flex-1 bg-red-500"></div>
      </div>
      <!-- Dark overlay retreating from the left as level grows, dimming the unlit portion -->
      <div
        aria-hidden="true"
        class="absolute inset-y-0 right-0 bg-gray-950/80"
        [style.width.%]="100 - levelPercent()"
      ></div>
      <!-- Peak-hold marker: thin line above the overlay so it stays visible after a spike -->
      @if (peakPercent() > 0) {
        <div
          aria-hidden="true"
          class="absolute inset-y-0 w-0.5"
          [class]="peakClass()"
          [style.left.%]="peakPercent()"
        ></div>
      }
    </div>
  `,
})
export class AudioLevelIndicator {
  private readonly destroyRef = inject(DestroyRef);

  readonly track = input<MediaStreamTrack | null>(null);

  protected readonly greenPct = GREEN_PCT;
  protected readonly orangePct = ORANGE_PCT;

  private readonly level = signal(0);
  private readonly peakLevel = signal(0);
  readonly levelPercent = computed(() => Math.round(this.level() * 100));
  readonly peakPercent = computed(() => Math.round(this.peakLevel() * 100));
  readonly peakClass = computed(() => {
    const p = this.peakPercent();
    if (p <= GREEN_PCT) return 'bg-green-500/80';
    if (p <= GREEN_PCT + ORANGE_PCT) return 'bg-orange-400/80';
    return 'bg-red-500/80';
  });

  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private animFrameId: number | null = null;
  private buffer: Uint8Array<ArrayBuffer> | null = null;

  // Smoothing state — lives outside signals to avoid spurious re-renders
  private smoothed = 0;
  private peak = 0;
  private peakHoldFrames = 0;

  constructor() {
    effect(() => {
      const track = this.track();
      this.teardown();
      if (track) {
        this.setup(track);
      }
    });

    this.destroyRef.onDestroy(() => this.teardown());
  }

  private setup(track: MediaStreamTrack): void {
    const mediaStream = new MediaStream([track]);
    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 1024;
    // smoothingTimeConstant only affects getByteFrequencyData — we handle
    // smoothing ourselves in tick() so there is no need to set it here.
    this.buffer = new Uint8Array(this.analyser.fftSize);
    this.source = this.audioContext.createMediaStreamSource(mediaStream);
    this.source.connect(this.analyser);
    this.animFrameId = requestAnimationFrame(() => this.tick());
  }

  private tick(): void {
    if (!this.analyser || !this.buffer) return;

    this.analyser.getByteTimeDomainData(this.buffer);

    let sum = 0;
    for (let i = 0; i < this.buffer.length; i++) {
      const v = (this.buffer[i] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / this.buffer.length);

    // Map RMS to dBFS with a −60 dB floor, then normalize to 0–1
    const db = rms > 0 ? 20 * Math.log10(rms) : -Infinity;
    const raw = isFinite(db) ? Math.max(0, Math.min(1, (db + 60) / 60)) : 0;

    // Asymmetric attack/release: fast on the way up, slow on the way down
    const alpha = raw > this.smoothed ? ATTACK : RELEASE;
    this.smoothed = raw * alpha + this.smoothed * (1 - alpha);
    this.level.set(this.smoothed);

    // Peak hold: freeze marker at highest recent value, then decay
    if (this.smoothed >= this.peak) {
      this.peak = this.smoothed;
      this.peakHoldFrames = PEAK_HOLD_FRAMES;
    } else if (this.peakHoldFrames > 0) {
      this.peakHoldFrames--;
    } else {
      this.peak *= PEAK_DECAY;
    }
    this.peakLevel.set(this.peak);

    this.animFrameId = requestAnimationFrame(() => this.tick());
  }

  private teardown(): void {
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    this.source?.disconnect();
    this.source = null;
    this.analyser = null;
    this.buffer = null;
    this.audioContext?.close();
    this.audioContext = null;
    this.smoothed = 0;
    this.peak = 0;
    this.peakHoldFrames = 0;
    this.level.set(0);
    this.peakLevel.set(0);
  }
}
